import React, { FC } from 'react';
import { FormInstance } from 'antd';
import { useBrandMapping } from 'hooks/useBrandMapping';
import { useClientMapping } from 'hooks/useClientMapping';
import { useSupplierMapping } from 'hooks/useSupplierMapping';
import { Button, Col, CommunicationHubEntityType, Form, Row, Select, Typography } from 'components';
import { AllEntityTypes, TypeOfEntityType } from 'models/ComunicationHub';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { CloseIcon, CloseIconLight, DoneIcon, EditIconLight } from 'components/common/icons';
import { RelationEntity } from './relationEntity';
import { useRelation } from './useRelation';
import styles from './styles.module.scss';

const { Item } = Form;
const { Text } = Typography;

export type RelationDataType = {
  relationEntity: string;
  relationType: TypeOfEntityType;
  id: string;
};

interface IRelation {
  onRemove: (id: string) => void;
  onEdit: (id: string, type: string) => void;
  onCancel: () => void;
  data: RelationDataType;
  entity: CommunicationHubEntityType;
  form: FormInstance;
  relationList: Record<string, any>[];
  onSave: (relation: any) => void;
  isEditeMode: boolean;
}

export const RelationCard: FC<IRelation> = React.memo((props) => {
  const { data, entity, onSave, relationList, form, onRemove, onEdit, isEditeMode, onCancel } = props;
  const { hasAccess } = useAccessControl();
  const { brandMapping } = useBrandMapping();
  const { supplierMapping } = useSupplierMapping();
  const { clientMapping } = useClientMapping();
  const { relationEntity, relationType } = data;
  const ENTITY_MAPPING: Record<string, any> = {
    BRAND: brandMapping,
    SUPPLIER: supplierMapping,
    CLIENT: clientMapping,
  };

  const { onEditRelationSave, onRelationEdit, onRelationRemove, onRouteChange } = useRelation({
    form,
    entity,
    relationList,
    onSave,
    onRemove,
    onEdit,
  });
  return (
    <Col span={24}>
      {!isEditeMode && (
        <Row className={styles.main} align={'middle'} justify={'space-between'}>
          <Col>
            <Row gutter={[4, 0]} align={'middle'}>
              <Col>
                <Text type={'secondary'} className={styles.relation_type_label}>
                  {AllEntityTypes[relationType]?.label}
                </Text>
              </Col>
              <Col>
                <Button
                  type={'link'}
                  variant={'link'}
                  style={{
                    color: '#222530',
                    textDecoration: 'underline',
                    padding: 0,
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                  onClick={() => onRouteChange(relationType, relationEntity as string)}
                >
                  {ENTITY_MAPPING[relationType]?.[relationEntity]?.name || relationEntity}
                </Button>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row align={'middle'}>
              <Col>
                <Button
                  size={'small'}
                  type={'link'}
                  icon={<EditIconLight />}
                  onClick={() => onRelationEdit(relationEntity, relationType)}
                />
              </Col>
              <Col>
                <Button
                  size={'small'}
                  type={'link'}
                  icon={<CloseIconLight />}
                  onClick={() => onRelationRemove(relationEntity, relationType)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      {isEditeMode && (
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item name="editRelationType" label={'Relation Type'}>
              <Select
                onChange={() => {
                  form.resetFields(['editRelationEntity']);
                }}
                options={Object.values(AllEntityTypes).filter((i) => hasAccess(i.permission) && i.value !== entity)}
                size={'large'}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item noStyle shouldUpdate={(prev, curr) => prev.editRelationType !== curr.editRelationType}>
              {({ getFieldValue }) => {
                const entity = getFieldValue('editRelationType');
                const entityId = getFieldValue('editRelationEntity');
                return <RelationEntity entity={entity} entityId={entityId} mode={'edit'} relations={relationList} />;
              }}
            </Item>
          </Col>
          <Col span={24}>
            <Row gutter={4} justify={'end'}>
              <Col>
                <Button color="default" variant="filled" onClick={onCancel} icon={<CloseIcon />} />
              </Col>
              <Col>
                <Button
                  onClick={() => onEditRelationSave(relationEntity, relationType)}
                  type={'primary'}
                  icon={<DoneIcon />}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </Col>
  );
});
